import { useCallback, useEffect, useRef } from 'react';
import { useSetAtom } from 'jotai';
import { playingMediaIdsAtom } from './atoms/media-atoms';
let mediaIdCounter = 0;
type MediaPlaybackOptionsT = {
  enableDomDetection?: boolean;
};
const processNode = (node: Node, action: (el: HTMLMediaElement) => void) => {
  if (node.nodeType !== Node.ELEMENT_NODE) return;
  const element = (node as Element);
  if (element.tagName === 'VIDEO' || element.tagName === 'AUDIO') {
    action((element as HTMLMediaElement));
  }
  const descendantMedia = element.querySelectorAll('video, audio');
  descendantMedia.forEach(media => action((media as HTMLMediaElement)));
};
const observeMediaMutations = (onAdd: (el: HTMLMediaElement) => void, onRemove: (el: HTMLMediaElement) => void) => {
  const observer = new MutationObserver(mutations => {
    mutations.forEach(mutation => {
      mutation.addedNodes.forEach(node => processNode(node, onAdd));
      mutation.removedNodes.forEach(node => processNode(node, onRemove));
    });
  });
  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
  return () => observer.disconnect();
};
export function useMediaPlayback(options: MediaPlaybackOptionsT = {}) {
  const {
    enableDomDetection = true
  } = options;
  const setPlayingMediaIds = useSetAtom(playingMediaIdsAtom);
  const notifiedMediaIdsRef = useRef<Set<string>>(new Set());
  const domMediaMapRef = useRef<Map<HTMLMediaElement, string>>(new Map());
  const mediaHandlerMapRef = useRef<Map<HTMLMediaElement, {
    play: () => void;
    pause: () => void;
    ended: () => void;
    seeked: () => void;
  }>>(new Map());
  const cleanup = useCallback(() => {
    if (notifiedMediaIdsRef.current.size === 0) return;
    setPlayingMediaIds(prev => {
      const next = new Set(prev);
      notifiedMediaIdsRef.current.forEach(id => next.delete(id));
      return next;
    });
    notifiedMediaIdsRef.current.clear();
  }, [setPlayingMediaIds]);
  const notifyMediaPlayback = useCallback((mediaId: string, isPlaying: boolean) => {
    if (isPlaying) {
      if (notifiedMediaIdsRef.current.has(mediaId)) return;
      notifiedMediaIdsRef.current.add(mediaId);
      setPlayingMediaIds(prev => {
        const next = new Set(prev);
        next.add(mediaId);
        return next;
      });
    } else {
      if (!notifiedMediaIdsRef.current.has(mediaId)) return;
      notifiedMediaIdsRef.current.delete(mediaId);
      setPlayingMediaIds(prev => {
        const next = new Set(prev);
        next.delete(mediaId);
        return next;
      });
    }
  }, [setPlayingMediaIds]);
  const notifyActivity = useCallback((activityType?: string) => {
    const event = new CustomEvent('auto-pause:activity', {
      detail: {
        activityType: activityType || 'custom',
        timestamp: Date.now()
      }
    });
    document.dispatchEvent(event);
  }, []);
  useEffect(() => {
    if (!enableDomDetection) return;
    const domMediaMap = domMediaMapRef.current;
    const handleMediaPlay = (element: HTMLMediaElement) => {
      const alreadyTracked = domMediaMap.has(element);
      const userActivationActive = navigator.userActivation?.isActive ?? true;
      if (alreadyTracked) return;
      // Skip autoplay — only track user-initiated playback
      if (!userActivationActive) return;
      const mediaId = `dom-media-${++mediaIdCounter}`;
      domMediaMap.set(element, mediaId);
      setPlayingMediaIds(prev => {
        return new Set([...prev, mediaId]);
      });
    };
    const untrackMedia = (element: HTMLMediaElement) => {
      const mediaId = domMediaMap.get(element);
      if (!mediaId) return;
      domMediaMap.delete(element);
      setPlayingMediaIds(prev => {
        const next = new Set(prev);
        next.delete(mediaId);
        return next;
      });
    };
    const handleLoopRestart = (element: HTMLMediaElement) => {
      if (!element.loop) return;
      if (element.currentTime > 1) return;
      untrackMedia(element);
    };
    const attachMediaListeners = (element: HTMLMediaElement) => {
      const playHandler = () => handleMediaPlay(element);
      const pauseHandler = () => untrackMedia(element);
      const endedHandler = () => untrackMedia(element);
      const seekedHandler = () => handleLoopRestart(element);
      element.addEventListener('play', playHandler);
      element.addEventListener('pause', pauseHandler);
      element.addEventListener('ended', endedHandler);
      element.addEventListener('seeked', seekedHandler);
      mediaHandlerMapRef.current.set(element, {
        play: playHandler,
        pause: pauseHandler,
        ended: endedHandler,
        seeked: seekedHandler
      });
    };
    const detachMediaListeners = (element: HTMLMediaElement) => {
      const handlers = mediaHandlerMapRef.current.get(element);
      if (!handlers) return;
      element.removeEventListener('play', handlers.play);
      element.removeEventListener('pause', handlers.pause);
      element.removeEventListener('ended', handlers.ended);
      element.removeEventListener('seeked', handlers.seeked);
      mediaHandlerMapRef.current.delete(element);
      untrackMedia(element);
    };
    const mediaElements = document.querySelectorAll('video, audio');
    mediaElements.forEach(element => attachMediaListeners((element as HTMLMediaElement)));
    const disconnectObserver = observeMediaMutations(attachMediaListeners, detachMediaListeners);
    return () => {
      disconnectObserver();
      domMediaMap.forEach((_, element) => {
        detachMediaListeners(element);
      });
      domMediaMap.clear();
    };
  }, [enableDomDetection, setPlayingMediaIds]);
  return {
    notifyMediaPlayback,
    notifyActivity,
    cleanup
  };
}